import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTenant } from './TenantContext';
import type { AccessibleProject } from '../types/data-models';

/**
 * The active project.
 *
 * Deliberately a narrower shape than `Project`: it is populated from
 * `/my/projects`, which returns only what a picker needs, and from freshly
 * created projects. Anything needing the full row fetches it by id.
 */
export interface ActiveProject {
  id: number;
  name: string;
  description?: string | null;
  /**
   * Owning workspace. Absent when the project was loaded from a
   * workspace-scoped endpoint, which by definition means the active one.
   */
  tenantId?: number;
  /** Owning workspace name — present when the project came from /my/projects. */
  tenantName?: string;
  /** 'own' vs 'shared'. Absent for a just-created project (always own). */
  access?: 'own' | 'shared';
  role?: AccessibleProject['role'];
  sharedBy?: AccessibleProject['sharedBy'];
}

interface ProjectContextValue {
  activeProject: ActiveProject | null;
  setActiveProject: (project: ActiveProject | null) => void;
  /**
   * Selecting a project in another workspace switches tenant first, which
   * changes `activeTenantId` and would otherwise clear the very selection we
   * are making. Call this immediately before the switch to protect it.
   */
  beginCrossTenantSelection: () => void;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

const STORAGE_KEY = 'console_active_project';

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { activeTenantId } = useTenant();

  const [activeProject, setActiveProjectState] = useState<ActiveProject | null>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored) as ActiveProject;
    } catch {
      // ignore
    }
    return null;
  });

  // Set while a deliberate cross-workspace selection is in flight, so the
  // tenant-change effect below doesn't wipe it. Without this, opening a shared
  // project flashes back to "Select project…": switchTenant changes the tenant,
  // the effect fires, and the selection is gone before the new one lands.
  const crossTenantSelection = useRef(false);

  // Tracks the tenant this effect last reacted to, so it can tell a genuine
  // tenant switch apart from its own mount run. Without this, the effect
  // (which React runs after the first commit too) wipes the sessionStorage-
  // restored activeProject on every page load/refresh, since activeTenantId
  // "changing" from undefined to its initial value looks identical to a real change.
  const lastTenantId = useRef(activeTenantId);

  // Projects are workspace-scoped — clear the selection whenever the workspace
  // changes, EXCEPT when the change is the switch we initiated to open a
  // project in that workspace.
  useEffect(() => {
    if (lastTenantId.current === activeTenantId) return;
    lastTenantId.current = activeTenantId;
    if (crossTenantSelection.current) {
      crossTenantSelection.current = false;
      return;
    }
    setActiveProjectState(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }, [activeTenantId]);

  const setActiveProject = useCallback((project: ActiveProject | null) => {
    setActiveProjectState(project);
    if (project) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(project));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const beginCrossTenantSelection = useCallback(() => {
    crossTenantSelection.current = true;
  }, []);

  const value = useMemo<ProjectContextValue>(
    () => ({ activeProject, setActiveProject, beginCrossTenantSelection }),
    [activeProject, setActiveProject, beginCrossTenantSelection],
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useActiveProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useActiveProject must be used inside ProjectProvider');
  return ctx;
}
